// The column list, defined once.
//
// The table and the Excel export used to declare their columns separately, so
// the sheet carried fields the page did not show and labelled them differently.
// Accounting reconciles the export against the invoice ledger, so a column that
// exists in one place under another name is a real cost, not an inconsistency
// worth shrugging at.
//
// Order and labels match the ledger sheet (reports/upi-invoice-ledger.xlsx)
// exactly, so the two can be read side by side -- except "Customer Name", which
// is shown beside "Billing Name" to expose the typed-KYC vs official-invoice
// difference at a glance.

export type Payment = {
  transactionId?: string | null;
  bankAlertRef?: string | null;
  date?: string | null;
  billingName?: string | null;
  /** Name typed into the KYC form; billingName is the official invoice name. */
  customerName?: string | null;
    topUpAmount?: number | null;             
    backendCharges: number | null;      

  
    topUpAmountGst: number | null;
    backendChargesGst: number | null;
  /** PD reconnection surcharge (Rs 372, GST-inclusive) when the payment bought a
   *  revival; null on an ordinary top-up, which the money formatter leaves blank. */
  pdCaseCharge?: number | null;
  amount?: number | null;
  months?: number | null;
  kycMode?: string | null;
  vin?: string | null;
  billingAddressWithPincode?: string | null;
  pincode?: string | null;
  gstDetails?: string | null;
  emailId?: string | null;
  panDetails?: string | null;
  contactNumber?: string | null;
  chequeRefNo?: string | null;
  remitterName?: string | null;
  dealerName?: string | null;
  dealerCode?: string | null;
  billTo?: string | null;
  monthsPaidFor?: number | null;
  chargeBasis?: string | null;
  rtoState?: string | null;
  vehicleModel?: string | null;
  imei?: string | null;
  iccid?: string | null;
  sessionId?: string | null;
  source?: string | null;
  paymentReceived?: boolean | null;
  isTestPayment?: boolean | null;
  missingForInvoice?: string | null;
  /** GENERATED / PENDING / FAILED / MANUAL / BEFORE_AUTOMATION / NOT_APPLICABLE */
  invoiceStatus?: string | null;
  invoiceNumber?: string | null;
  invoiceError?: string | null;
};

export type PendingPayment = {
  sessionId?: string | null;
  date?: string | null;
  source?: string | null;
  kycMode?: string | null;
  months?: number | null;
  status?: string | null;
  /** Name typed into the KYC form; billingName is the official invoice name. */
  customerName?: string | null;
  billingName?: string | null;
  contactNumber?: string | null;
  vin?: string | null;
  imei?: string | null;
  vehicleModel?: string | null;
  dealerName?: string | null;
  dealerCode?: string | null;
  rtoState?: string | null;
  expectedAmount?: number | null;
};

export type Column<T> = {
  label: string;
  /** Cell value for both the table and the export. Never formatted currency —
   *  Excel must receive a number it can sum, so money is formatted at render. */
  value: (row: T) => string | number | null | undefined;
  align?: "left" | "right" | "center";
  money?: boolean;
  width?: number;
  /** Wide free text (an address, a basis line) that must not force one
   *  enormous column. */
  wrap?: boolean;
};

export const fmtDateTime = (value?: string | number | null): string => {
  if (value === null || value === undefined || value === "") return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("en-IN", {
    // Pinned, not left to the viewer's machine. Without it toLocaleString
    // formats in whatever zone the browser sits in, so the same bank
    // transaction reads as a different time for a colleague abroad -- and these
    // timestamps are reconciled against HDFC statements, which are always IST.
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const fmtMoney = (value?: number | null): string =>
  typeof value === "number"
    ? value.toLocaleString("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      })
    : "";

// What the Invoice Status column shows. The server's values are stable
// identifiers; these are the words accounting reads.
export const INVOICE_STATUS_LABEL: Record<string, string> = {
  GENERATED: "Generated",
  PENDING: "Yet to generate",
  FAILED: "Failed",
  MANUAL: "Manual",
  BEFORE_AUTOMATION: "Manual (before 18 Sep)",
  NOT_APPLICABLE: "N/A",
};

export const PAYMENT_COLUMNS: Column<Payment>[] = [
  { label: "Transaction Date", value: (r) => fmtDateTime(r.date), width: 150 },
  { label: "Billing Name", value: (r) => r.billingName, width: 200 },
  // The name the customer typed into the KYC form. Kept beside the official
  // Billing Details name so a mismatch (a typed job title, say) is visible
  // rather than silently overwritten.
  { label: "Customer Name", value: (r) => r.customerName, width: 200 },
  // Beside the name, as on the ledger sheet: whether Zoho has the document
  // yet, and its number when it does. A FAILED row carries the reason.
  {
    label: "Invoice Status",
    value: (r) =>
      r.invoiceStatus
        ? (INVOICE_STATUS_LABEL[r.invoiceStatus] ?? r.invoiceStatus) +
          (r.invoiceStatus === "FAILED" && r.invoiceError ? ` — ${r.invoiceError}` : "")
        : "",
    align: "center",
    width: 150,
    wrap: true,
  },
  { label: "Invoice No", value: (r) => r.invoiceNumber, width: 150 },
  { label: "Top-up Amount", value: (r) => r.topUpAmount, align: "right", money: true, width: 130 },
  { label: "Top-up Amount GST", value: (r) => r.topUpAmountGst, align: "right", money: true, width: 130 },
  { label: "Backend Charges", value: (r) => r.backendCharges, align: "right", money: true, width: 140 },
  { label: "Backend Charges GST", value: (r) => r.backendChargesGst, align: "right", money: true, width: 140 },
  // Directly before the total, so the money columns to its left add up to it.
  { label: "PD Case Charges", value: (r) => r.pdCaseCharge, align: "right", money: true, width: 130 },
  { label: "Total Amount", value: (r) => r.amount, align: "right", money: true, width: 130 },
  { label: "Months", value: (r) => r.months, align: "center", width: 80 },
  { label: "KYC Mode", value: (r) => r.kycMode, align: "center", width: 100 },
  { label: "VIN / Chassis No", value: (r) => r.vin, width: 180 },
  { label: "Billing Address with Pincode", value: (r) => r.billingAddressWithPincode, width: 280, wrap: true },
  { label: "Pincode", value: (r) => r.pincode, align: "center", width: 90 },
  { label: "GST Details", value: (r) => r.gstDetails, width: 150 },
  { label: "Email ID", value: (r) => r.emailId, width: 180 },
  { label: "PAN Details", value: (r) => r.panDetails, width: 130 },
  { label: "Contact Number", value: (r) => r.contactNumber, width: 130 },
  { label: "Cheque / Ref No", value: (r) => r.chequeRefNo, width: 140 },
  { label: "Bank Alert Ref", value: (r) => r.bankAlertRef, width: 190 },
  { label: "Remitter Name (bank)", value: (r) => r.remitterName, width: 180 },
  { label: "Dealer Name", value: (r) => r.dealerName, width: 200 },
  { label: "Dealer Code", value: (r) => r.dealerCode, align: "center", width: 110 },
  { label: "Bill To", value: (r) => r.billTo, align: "center", width: 100 },
  { label: "Months Paid For", value: (r) => r.monthsPaidFor, align: "center", width: 120 },
  { label: "Charge Basis", value: (r) => r.chargeBasis, width: 300, wrap: true },
  { label: "RTO State", value: (r) => r.rtoState, width: 150 },
  { label: "Vehicle Model", value: (r) => r.vehicleModel, width: 140 },
  { label: "IMEI", value: (r) => r.imei, width: 160 },
  { label: "ICCID", value: (r) => r.iccid, width: 190 },
  { label: "Session ID", value: (r) => r.sessionId, width: 120 },
  { label: "Source", value: (r) => r.source, width: 150 },
  {
    label: "Payment Marked Received",
    value: (r) => (r.paymentReceived ? "Yes" : "No"),
    align: "center",
    width: 150,
  },
  { label: "Missing For Invoice", value: (r) => r.missingForInvoice, width: 240, wrap: true },
];

export const PENDING_COLUMNS: Column<PendingPayment>[] = [
  { label: "Requested On", value: (r) => fmtDateTime(r.date), width: 150 },
  { label: "Session ID", value: (r) => r.sessionId, width: 120 },
  { label: "Customer Name", value: (r) => r.customerName, width: 200 },
  { label: "Billing Name", value: (r) => r.billingName, width: 200 },
  { label: "Contact Number", value: (r) => r.contactNumber, width: 130 },
  { label: "VIN / Chassis No", value: (r) => r.vin, width: 180 },
  { label: "KYC Mode", value: (r) => r.kycMode, align: "center", width: 100 },
  { label: "Months", value: (r) => r.months, align: "center", width: 80 },
  // Null, never 0, when the RTO state is unknown -- a zero here would read as
  // "this session owes nothing".
  { label: "Expected Amount", value: (r) => r.expectedAmount, align: "right", money: true, width: 140 },
  { label: "RTO State", value: (r) => r.rtoState, width: 150 },
  { label: "Vehicle Model", value: (r) => r.vehicleModel, width: 140 },
  { label: "IMEI", value: (r) => r.imei, width: 160 },
  { label: "Dealer Name", value: (r) => r.dealerName, width: 200 },
  { label: "Dealer Code", value: (r) => r.dealerCode, align: "center", width: 110 },
  { label: "Source", value: (r) => r.source, width: 150 },
  { label: "Status", value: (r) => r.status, align: "center", width: 160 },
];
